import React, { useEffect, useMemo, useState } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import FormVideoUpload from '../../forms/FormVideoUpload'
import FormMultiVideoUpload from '../../forms/FormMultiVideoUpload'
import { VideoType } from '../../../utils/videoUtils'
import { supabase } from '../../../services/supabase'

interface VideoUploadModalProps {
  isOpen: boolean
  onClose: () => void
  onSubmit: (videoUrl: string | null, videoType?: VideoType, additionalVideos?: string[]) => Promise<void>
  productName: string
  isLoading?: boolean
  allowMultiple?: boolean
  maxVideos?: number
  existingPrimaryVideoUrl?: string | null
  existingAdditionalVideos?: string[] | null
}

/**
 * Modal for uploading videos to products/inventory
 * Used by sellers to add video content to their listings
 */
export const VideoUploadModal: React.FC<VideoUploadModalProps> = ({
  isOpen,
  onClose,
  onSubmit,
  productName,
  isLoading = false,
  allowMultiple = true,
  maxVideos = 5,
  existingPrimaryVideoUrl = null,
  existingAdditionalVideos = []
}) => {
  const [primaryVideo, setPrimaryVideo] = useState<File | null>(null)
  const [additionalVideos, setAdditionalVideos] = useState<File[]>([])
  const [keptPrimaryVideoUrl, setKeptPrimaryVideoUrl] = useState<string | null>(null)
  const [keptAdditionalVideoUrls, setKeptAdditionalVideoUrls] = useState<string[]>([])
  const [videoType, setVideoType] = useState<VideoType>(VideoType.PRODUCT_DEMO)
  const [uploadProgress, setUploadProgress] = useState<{ [key: string]: number }>({})
  const [error, setError] = useState<string | null>(null)
  const [success, setSuccess] = useState(false)
  const [isSubmitting, setIsSubmitting] = useState(false)

  const isBusy = isLoading || isSubmitting
  const normalizeUniqueUrls = (urls: Array<string | null | undefined>): string[] => {
    const seen = new Set<string>()
    const result: string[] = []

    urls.forEach((url) => {
      if (typeof url !== 'string') {
        return
      }

      const trimmed = url.trim()
      if (!trimmed || seen.has(trimmed)) {
        return
      }

      seen.add(trimmed)
      result.push(trimmed)
    })

    return result
  }

  const existingVideoState = useMemo(() => {
    const rawPrimary =
      typeof existingPrimaryVideoUrl === 'string' && existingPrimaryVideoUrl.trim().length > 0
        ? existingPrimaryVideoUrl.trim()
        : null

    const rawAdditional = normalizeUniqueUrls(existingAdditionalVideos ?? [])
    const all = normalizeUniqueUrls([rawPrimary, ...rawAdditional])
    const primary = rawPrimary ?? all[0] ?? null
    const additional = all.filter((url) => url !== primary)
    const canonicalRawAdditional = rawAdditional.filter((url) => url !== primary)
    const needsCanonicalization =
      rawPrimary !== primary ||
      JSON.stringify(canonicalRawAdditional) !== JSON.stringify(additional)

    return {
      primary,
      additional,
      needsCanonicalization
    }
  }, [existingPrimaryVideoUrl, existingAdditionalVideos])

  useEffect(() => {
    if (!isOpen) {
      return
    }

    setPrimaryVideo(null)
    setAdditionalVideos([])
    setKeptPrimaryVideoUrl(existingVideoState.primary)
    setKeptAdditionalVideoUrls(existingVideoState.additional)
    setUploadProgress({})
    setError(null)
    setSuccess(false)
  }, [isOpen, existingVideoState])

  const hasChanges =
    existingVideoState.needsCanonicalization ||
    primaryVideo !== null ||
    additionalVideos.length > 0 ||
    keptPrimaryVideoUrl !== existingVideoState.primary ||
    JSON.stringify(keptAdditionalVideoUrls) !== JSON.stringify(existingVideoState.additional)

  const resolveBucket = (type: VideoType): string => {
    if (
      type === VideoType.INSTALLATION ||
      type === VideoType.FITTING_GUIDE ||
      type === VideoType.MAINTENANCE
    ) {
      return 'tyre-videos'
    }

    if (type === VideoType.WORKSHOP_TOUR || type === VideoType.CUSTOMER_TESTIMONIAL) {
      return 'business-videos'
    }

    if (type === VideoType.COMPARISON) {
      return 'product-videos'
    }

    return 'product-videos'
  }

  const sanitizeFileName = (name: string): string =>
    name.toLowerCase().replace(/[^a-z0-9.\-_]+/g, '-').replace(/-+/g, '-')

  const uploadVideoFile = async (file: File, bucket: string): Promise<string> => {
    const timestamp = Date.now()
    const safeName = sanitizeFileName(file.name)
    const filePath = `${videoType}/${timestamp}-${safeName}`

    setUploadProgress((prev) => ({ ...prev, [file.name]: 10 }))

    const { data, error: uploadError } = await supabase.storage
      .from(bucket)
      .upload(filePath, file, {
        cacheControl: '3600',
        upsert: false,
        contentType: file.type || 'video/mp4'
      })

    if (uploadError) {
      setUploadProgress((prev) => ({ ...prev, [file.name]: 0 }))
      throw uploadError
    }

    const { data: publicUrlData } = supabase.storage.from(bucket).getPublicUrl(data.path)
    setUploadProgress((prev) => ({ ...prev, [file.name]: 100 }))
    return publicUrlData.publicUrl
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    setError(null)
    setIsSubmitting(true)

    try {
      const bucket = resolveBucket(videoType)
      let finalPrimaryVideoUrl: string | null = keptPrimaryVideoUrl
      let finalAdditionalVideoUrls: string[] = normalizeUniqueUrls(keptAdditionalVideoUrls)

      if (primaryVideo) {
        finalPrimaryVideoUrl = await uploadVideoFile(primaryVideo, bucket)
      }

      for (const video of additionalVideos) {
        const uploadedUrl = await uploadVideoFile(video, bucket)
        finalAdditionalVideoUrls.push(uploadedUrl)
      }

      finalAdditionalVideoUrls = normalizeUniqueUrls(finalAdditionalVideoUrls)

      if (finalPrimaryVideoUrl) {
        finalAdditionalVideoUrls = finalAdditionalVideoUrls.filter((url) => url !== finalPrimaryVideoUrl)
      }

      if (!finalPrimaryVideoUrl && finalAdditionalVideoUrls.length > 0) {
        finalPrimaryVideoUrl = finalAdditionalVideoUrls[0]
        finalAdditionalVideoUrls = finalAdditionalVideoUrls.slice(1)
      }

      if (!allowMultiple) {
        finalAdditionalVideoUrls = []
      }

      await onSubmit(
        finalPrimaryVideoUrl,
        videoType,
        allowMultiple ? finalAdditionalVideoUrls : undefined
      )

      setSuccess(true)
      setTimeout(() => {
        resetForm()
        onClose()
      }, 2000)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to upload videos')
    } finally {
      setIsSubmitting(false)
    }
  }

  const resetForm = () => {
    setPrimaryVideo(null)
    setAdditionalVideos([])
    setKeptPrimaryVideoUrl(existingVideoState.primary)
    setKeptAdditionalVideoUrls(existingVideoState.additional)
    setVideoType(VideoType.PRODUCT_DEMO)
    setUploadProgress({})
    setError(null)
    setSuccess(false)
  }

  const handleClose = () => {
    if (isBusy) return
    resetForm()
    onClose()
  }

  if (!isOpen) return null

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4"
        onClick={handleClose}
      >
        <motion.div
          initial={{ opacity: 0, scale: 0.95, y: 20 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.95, y: -20 }}
          onClick={(e) => e.stopPropagation()}
          className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto"
        >
          {/* Header */}
          <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between">
            <div>
              <h2 className="text-xl font-bold text-gray-900">Upload Videos</h2>
              <p className="text-sm text-gray-600 mt-1">{productName}</p>
            </div>
            <motion.button
              whileHover={{ scale: 1.1 }}
              whileTap={{ scale: 0.95 }}
              onClick={handleClose}
              className="p-2 text-gray-500 hover:text-gray-700 transition-colors"
            >
              <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </motion.button>
          </div>

          {/* Content */}
          <form id="video-upload-form" onSubmit={handleSubmit} className="px-6 py-4 space-y-6">
            {/* Success message */}
            {success && (
              <motion.div
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                className="p-4 bg-green-50 border border-green-200 rounded-lg flex items-center gap-3"
              >
                <svg className="h-5 w-5 text-green-600" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                </svg>
                <div>
                  <p className="font-medium text-green-900">Videos uploaded successfully!</p>
                  <p className="text-sm text-green-800">Redirecting in a moment...</p>
                </div>
              </motion.div>
            )}

            {/* Error message */}
            {error && (
              <motion.div
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                className="p-4 bg-red-50 border border-red-200 rounded-lg"
              >
                <p className="text-red-800 text-sm">{error}</p>
              </motion.div>
            )}

            {(keptPrimaryVideoUrl || keptAdditionalVideoUrls.length > 0) && (
              <div className="p-4 border border-gray-200 rounded-lg bg-gray-50 space-y-4">
                <h3 className="text-sm font-semibold text-gray-900">Existing Videos</h3>

                {keptPrimaryVideoUrl && (
                  <div className="space-y-2">
                    <p className="text-xs font-medium text-gray-700">Current Primary Video</p>
                    <video
                      src={keptPrimaryVideoUrl}
                      className="w-full max-h-56 rounded border border-gray-200 bg-black"
                      controls
                      preload="metadata"
                    />
                    <div className="flex flex-wrap gap-2">
                      <button
                        type="button"
                        onClick={() => {
                          const input = document.getElementById('primary_video') as HTMLInputElement | null
                          input?.click()
                          setError(null)
                        }}
                        className="px-3 py-1.5 text-xs font-medium rounded border border-blue-300 text-blue-700 bg-white hover:bg-blue-50"
                      >
                        Replace Primary
                      </button>
                      <button
                        type="button"
                        onClick={() => setKeptPrimaryVideoUrl(null)}
                        className="px-3 py-1.5 text-xs font-medium rounded border border-red-300 text-red-700 bg-white hover:bg-red-50"
                      >
                        Delete Primary
                      </button>
                    </div>
                  </div>
                )}

                {keptAdditionalVideoUrls.length > 0 && (
                  <div className="space-y-2">
                    <p className="text-xs font-medium text-gray-700">Existing Additional Videos</p>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      {keptAdditionalVideoUrls.map((url, index) => (
                        <div key={`${url}-${index}`} className="border border-gray-200 rounded p-2 bg-white">
                          <video
                            src={url}
                            className="w-full h-32 rounded bg-black object-contain"
                            controls
                            preload="metadata"
                          />
                          <button
                            type="button"
                            onClick={() => {
                              setKeptAdditionalVideoUrls((prev) => prev.filter((_, i) => i !== index))
                            }}
                            className="mt-2 px-2.5 py-1 text-xs font-medium rounded border border-red-300 text-red-700 bg-white hover:bg-red-50"
                          >
                            Delete
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Video type selector */}
            <div>
              <label className="block text-sm font-medium text-gray-900 mb-2">
                Video Type
              </label>
              <select
                value={videoType}
                onChange={(e) => setVideoType(e.target.value as VideoType)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none"
              >
                <option value="product_demo">Product Demo</option>
                <option value="installation">Installation Guide</option>
                <option value="fitting_guide">Fitting Guide</option>
                <option value="maintenance">Maintenance Guide</option>
                <option value="comparison">Comparison</option>
                <option value="customer_testimonial">Customer Testimonial</option>
              </select>
            </div>

            {/* Primary video upload */}
            <FormVideoUpload
              label="Primary Video"
              name="primary_video"
              value={primaryVideo}
              onChange={(file) => {
                setPrimaryVideo(file)
                setError(null)
              }}
              maxSize={500}
              helpText={
                keptPrimaryVideoUrl
                  ? 'Upload a new file to replace the current primary video'
                  : 'This is the main video displayed for the product'
              }
              uploadProgress={uploadProgress[primaryVideo?.name ?? ''] ?? 0}
              isUploading={isBusy}
              required
            />

            {/* Additional videos */}
            {allowMultiple && (
              <FormMultiVideoUpload
                label="Additional Videos (Optional)"
                name="additional_videos"
                value={additionalVideos}
                onChange={(files) => {
                  setAdditionalVideos(files)
                  setError(null)
                }}
                maxVideos={Math.max(0, maxVideos - 1 - keptAdditionalVideoUrls.length)}
                maxSize={500}
                helpText="Upload additional videos like installation guides, reviews, or comparisons"
                uploadProgress={uploadProgress}
                isUploading={isBusy}
              />
            )}

            {/* Info box */}
            <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
              <h4 className="font-medium text-blue-900 text-sm mb-2">Video Guidelines</h4>
              <ul className="text-xs text-blue-800 space-y-1">
                <li>• Supported formats: MP4, WebM, OGG, MOV</li>
                <li>• Maximum file size: 500MB per video</li>
                <li>• Recommended resolution: 1920x1080 (1080p)</li>
                <li>• Keep primary video under 2 minutes</li>
                <li>• Use clear, well-lit footage for best results</li>
              </ul>
            </div>
          </form>

          {/* Footer */}
          <div className="sticky bottom-0 bg-gray-50 border-t border-gray-200 px-6 py-4 flex gap-3 justify-end">
            <motion.button
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              type="button"
              onClick={handleClose}
              disabled={isBusy}
              className="px-4 py-2 text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-100 transition-colors disabled:opacity-50"
            >
              Cancel
            </motion.button>
            <motion.button
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              type="submit"
              form="video-upload-form"
              disabled={isBusy || success || !hasChanges}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isBusy ? (
                <span className="flex items-center gap-2">
                  <svg className="h-4 w-4 animate-spin" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Uploading...
                </span>
              ) : (
                'Save Video Changes'
              )}
            </motion.button>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  )
}

export default VideoUploadModal
